// src/features/consultation/triageAllergyReconciliation.ts
// Whether the nurse's free-text Pre-Consult allergy note
// (stage_data.pre_consult.allergies) has anything to say that isn't already
// on the patient's structured allergy record — the Consultation redesign's
// Phase 2 (docs/HMS_V4_CONSULTATION_REDESIGN_PLAN.md). Same discipline as
// allergyAlerts.ts: a projection of rows, not a rules engine — this compares
// two things already on the wire, it doesn't diagnose anything.
import type { Allergy } from './types';
import { NO_KNOWN_ALLERGIES } from './useAllergyStatus';

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * A loose substring match, either direction, so "Dust" (nurse) matches
 * "Dust Allergy" (record) and "PAND D" (nurse) matches "PAND D Allergy"
 * (record) regardless of which side happened to be typed more precisely.
 */
export function matchesRecordedAllergy(triageText: string, allergies: Allergy[] | undefined): boolean {
  const norm = normalize(triageText);
  if (!norm) return true; // nothing to reconcile
  const active = (allergies ?? []).filter(
    a => a.substance !== NO_KNOWN_ALLERGIES && a.clinical_status === 'active'
  );
  return active.some(a => {
    const sub = normalize(a.substance);
    return !!sub && (norm.includes(sub) || sub.includes(norm));
  });
}

export interface TriagePreConsultAllergyFields {
  allergies?: string | null;
  allergies_reconciled?: boolean;
}

/**
 * The triage text to show in the review chip, or null when there's nothing
 * to review: no text was recorded, it was already dismissed as "not an
 * allergy", the allergy record hasn't loaded yet (say nothing rather than
 * guess — same rule buildAllergyAlerts follows), or it already matches
 * something already on the structured record.
 */
export function needsTriageReview(
  preConsult: TriagePreConsultAllergyFields | null | undefined,
  allergies: Allergy[] | undefined,
  reviewed: boolean
): string | null {
  const text = preConsult?.allergies?.trim();
  if (!text || preConsult?.allergies_reconciled || !reviewed) return null;
  return matchesRecordedAllergy(text, allergies) ? null : text;
}
